import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Client } from '../../http';
import { RootState } from '../../app/store';

export interface Plan {
  id: string;
  name: string;
  rsp: string;
  description1: string;
  description2: string;
  speed: number;
  // Typical evening speeds in Mbps split per access technology: passive
  // (GPON) buildings vs active ethernet.
  evening_download_passive: number;
  evening_upload_passive: number;
  evening_download_ae: number;
  evening_upload_ae: number;
  // The price shown on the signup site. Derived: the RRP less the plan
  // coupon.
  cost: string;
  // The full monthly price, which is the price of the Chargebee plan with
  // the plan's id. Read from Chargebee; typed here only when that plan does
  // not exist yet and saving creates it.
  // Empty on plans from before the RRP.
  rrp: string;
  addressLIT: boolean;
  addressFTTP: boolean;
  addressNonLIT: boolean;
  published: boolean;
  // The plan's own Chargebee coupon, applied to every signup on the plan on
  // top of any promo code the customer enters, and the switch for it.
  embedded_coupon: string;
  apply_embedded_coupon: boolean;
  // The plan's promo code: applied only when the customer brings it, in the
  // signup URL or typed at checkout, on top of the plan coupon. promo_cost
  // is the price with it, derived like cost; the signup card shows it in
  // place of cost, with the code. Empty when there is none.
  promo_code: string;
  apply_promo_code: boolean;
  promo_cost: string;
  // The network the plan is sold on. Empty is DGtek, as every plan was
  // before Community Fibre.
  network: string;
  // Advertised speeds when they differ. Symmetric plans carry neither and
  // are described by speed alone.
  download: number;
  upload: number;
  // The wholesaler's product id for this plan, per access technology
  // (fttp, gfast). A tier that does not exist on a technology has no id
  // for it and is then not offered at addresses served by it.
  external_plan_ids: Record<string, string>;
  // The wholesaler's Layer 2 product id per technology: what Community
  // Fibre's NPIS surface orders by. external_plan_ids hold the Layer 3 ids
  // the MEF surface (the default) orders by.
  layer2_plan_ids: Record<string, string>;
}

export interface plansState {
  value: Array<Plan>;
  status: 'idle' | 'loading' | 'failed';
}

export const newPlan = (): Plan => ({
  id: '',
  name: '',
  rsp: 'pineapple',
  description1: '',
  description2: '',
  speed: 0,
  evening_download_passive: 0,
  evening_upload_passive: 0,
  evening_download_ae: 0,
  evening_upload_ae: 0,
  cost: '0.00',
  rrp: '',
  addressLIT: true,
  addressNonLIT: false,
  addressFTTP: false,
  published: false,
  embedded_coupon: '',
  apply_embedded_coupon: false,
  promo_code: '',
  apply_promo_code: false,
  promo_cost: '',
  network: '',
  download: 0,
  upload: 0,
  external_plan_ids: {},
  layer2_plan_ids: {},
});

/**
 * planFromApi fills in whatever the admin API left out. Empty fields are
 * omitempty on the Go side, so a plan with no coupon, no network and no
 * wholesale ids arrives as a handful of keys; the form holds the plan as it
 * came, and a missing string took the page down the moment anything called
 * .trim() on it. A null is treated as absent for the same reason.
 */
export const planFromApi = (plan: Partial<Plan>): Plan => {
  const given = Object.fromEntries(
    Object.entries(plan ?? {}).filter(([, v]) => v !== null && v !== undefined),
  );
  const filled = { ...newPlan(), ...given } as Plan;
  // The API has no empty decimal: a plan without an RRP arrives with zero.
  if (Number(filled.rrp) === 0) filled.rrp = '';
  if (Number(filled.promo_cost) === 0) filled.promo_cost = '';
  return filled;
};

const initialState: plansState = {
  value: [],
  status: 'idle',
};

export const fetchPlans = createAsyncThunk('plans/fetchPlans', async () => {
  const response = await Client.get<Plan[]>('/api/v1/admin/plans');
  return response.data;
});

export const createPlan = createAsyncThunk<Plan, Plan, { state: RootState }>(
  'plans/createPlan',
  async (plan: Plan) => {
    const response = await Client.post<Plan>(
      '/api/v1/admin/plans/create',
      plan,
    );
    return response.data;
  },
);

export const updatePlan = createAsyncThunk<Plan, Plan, { state: RootState }>(
  'plans/updatePlan',
  async (plan: Plan) => {
    const response = await Client.post<Plan>(
      '/api/v1/admin/plans/update',
      plan,
    );
    return response.data;
  },
);

export const deletePlan = createAsyncThunk<Plan, Plan, { state: RootState }>(
  'plans/deletePlan',
  async (plan: Plan) => {
    await Client.post<Plan>('/api/v1/admin/plans/delete', plan);
    return plan;
  },
);

export const plansSlice = createSlice({
  name: 'plans',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.status = 'idle';
        state.value = (action.payload ?? []).map(planFromApi);
      })
      .addCase(fetchPlans.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(createPlan.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createPlan.fulfilled, (state, action) => {
        state.status = 'idle';
        state.value.push(planFromApi(action.payload));
      })
      .addCase(createPlan.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(updatePlan.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        state.status = 'idle';
        const saved = planFromApi(action.payload);
        state.value = state.value.map((v) => (v.id === saved.id ? saved : v));
      })
      .addCase(updatePlan.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(deletePlan.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deletePlan.fulfilled, (state, action) => {
        state.status = 'idle';
        state.value = state.value.flatMap((v) => (v.id === action.payload.id ? [] : [v]));
      })
      .addCase(deletePlan.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const selectPlans = (state: RootState) => state.plans.value;
export const selectPlanById = (id: string) => (state: RootState) => (
  state.plans.value.find((p) => p.id === id)
);
export default plansSlice.reducer;
